import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useGesture } from '@use-gesture/react';

import { DiagonalHatchDefinition } from '@da-g3-acl/shared/common';
import { HEX, RGB } from '@da-g3-acl/shared/common/colour-types';

import { isApple } from '../../../../helpers/platformFunctions';
import { sanitiseId } from '../../../../helpers/sanitiseId';
import { useThrottle } from '../../../../hooks/use-throttle';
import { isYIQDark, RGBToHex } from '../../../../types/colour';
import { LengthRange } from '../../../../types/lengthRange';
import { useGradients } from '../NumericPointsLaneRow/useGradients';
import {
	IntervalLaneRowConfiguration,
	IntervalLaneRowInterval,
	IntervalLaneRowIntervalValue,
	IntervalResizeHandle,
} from '../configurations';
import { LoadingGradientDefinition } from '../svgFills';

import { IntervalLaneRowContext } from './IntervalLaneRowProvider';
import { assignOverlapTracks } from './assignOverlapTracks';
import styles from './index.module.scss';

export interface IntervalLaneRowProps {
	id: string;
	width: number;
	scale: number;
	lane: LengthRange;
	configuration: IntervalLaneRowConfiguration;
	heightInPx: number;
	position: number;
	allowableEditRanges?: LengthRange[];
}

const TEXT_SIZE_THRESHOLD = 10;
const DRAG_THRESHOLD_PX_SQ = 9; // 3px radius dead-zone to distinguish click from drag

const getIntervalColour = (
	interval: IntervalLaneRowIntervalValue,
	getColourForRealValue: (realValue: number) => RGB,
): HEX =>
	// Use either the interval's specific colour, or if undefined - generate the colour using the provider for the interval's value
	interval.colour ?? RGBToHex(getColourForRealValue(interval.value ?? 0));

const defaultIntervalValue: IntervalLaneRowIntervalValue = {
	value: 1,
	colour: '#f80000',
	label: '<Unknown>',
};

const IntervalLaneRowInternal: React.FC<IntervalLaneRowProps> = ({
	scale,
	lane,
	configuration,
	heightInPx,
	position,
	width,
	allowableEditRanges,
}) => {
	const { onDoubleClick, onSingleClick, onHover } = configuration;
	const { handleIntervalResizeStarted, intervalsData, handleIntervalDrawStarted } =
		useContext(IntervalLaneRowContext);
	const { intervals, isLoading } = intervalsData[configuration.intervalSetId];

	const bounds = configuration.valueBounds;

	const { getColourForRealValue } = useGradients(configuration.colourProvider?.(bounds), bounds);

	const intervalsToRender = useMemo(
		() =>
			intervals
				.filter((i) => {
					const r = i.renderableDepth;
					return (
						// Interval starts/ends in our range
						[r.depthFrom, r.depthTo].find((d) => d >= lane.depthFrom && d <= lane.depthTo) !== undefined ||
						// Interval overlaps our range entirely
						(r.depthFrom <= lane.depthFrom && r.depthTo >= lane.depthTo)
					);
				})
				.sort((a, b) => a.renderableDepth.depthFrom - b.renderableDepth.depthFrom),
		[intervals, lane.depthFrom, lane.depthTo],
	);

	const trackAssignments = useMemo(
		() =>
			assignOverlapTracks(
				intervalsToRender,
				(i) => i.renderableDepth,
				(i) => i.values?.[0]?.label,
			),
		[intervalsToRender],
	);

	const { trackHeight, displayTrackCount } = useMemo(() => {
		const dataDrivenMaxTrackCount =
			trackAssignments.size > 0 ? trackAssignments.values().next().value?.maxTrackCount || 1 : 1;
		const displayTrackCount = Math.max(1, configuration.maxOverlapCount ?? 1, dataDrivenMaxTrackCount);
		return { trackHeight: heightInPx / displayTrackCount, displayTrackCount };
	}, [trackAssignments, heightInPx, configuration.maxOverlapCount]);

	const intervalsToRenderSignature = useMemo(
		() =>
			intervalsToRender.reduce(
				(prev, current) => `${prev}|${current.renderableDepth.depthFrom}::${current.renderableDepth.depthTo}`,
				'',
			),
		[intervalsToRender],
	);

	const svgRef = useRef<SVGSVGElement>(null);
	const mouseDownPosRef = useRef<{ x: number; y: number }>();

	const handleDrawMouseDown = useCallback(
		(e: React.MouseEvent<SVGElement>) => {
			const elementBounds = svgRef.current?.getBoundingClientRect();
			if (!elementBounds) return;
			const clickDepthInM = lane.depthFrom + (e.clientX - elementBounds.x) / scale;
			handleIntervalDrawStarted(configuration.intervalSetId, clickDepthInM, isApple ? e.metaKey : e.ctrlKey);
		},
		[configuration.intervalSetId, handleIntervalDrawStarted, lane.depthFrom, scale],
	);

	const findIntervalAtPosition = useCallback(
		(depth: number, yFromTop: number): IntervalLaneRowInterval | undefined => {
			if (displayTrackCount <= 1) {
				return intervalsToRender.find(
					(i) => i.renderableDepth.depthFrom < depth && i.renderableDepth.depthTo > depth,
				);
			}
			const clickedTrack = Math.min(Math.floor(yFromTop / trackHeight), displayTrackCount - 1);
			return intervalsToRender.find((i) => {
				const assignment = trackAssignments.get(i);
				if (!assignment || assignment.trackIndex !== clickedTrack) return false;
				return i.renderableDepth.depthFrom < depth && i.renderableDepth.depthTo > depth;
			});
		},
		[intervalsToRender, trackAssignments, trackHeight, displayTrackCount],
	);

	const handleDoubleClickInternal = useCallback(
		([x, y]: [number, number]) => {
			if (!onDoubleClick) return;
			const calculatedY = lane.depthFrom + x;
			const interval = findIntervalAtPosition(calculatedY, y);

			const intervalColours = interval?.values?.map((iv) => getIntervalColour(iv, getColourForRealValue));
			onDoubleClick(calculatedY, interval, intervalColours);
		},
		// eslint-disable-next-line
		[intervalsToRenderSignature, lane.depthFrom, onDoubleClick, findIntervalAtPosition],
	);

	const handleSingleClickInternal = useCallback(
		([x, y]: [number, number]) => {
			if (!onSingleClick) return;
			const calculatedY = lane.depthFrom + x;
			const interval = findIntervalAtPosition(calculatedY, y);

			const intervalColours = interval?.values?.map((iv) => getIntervalColour(iv, getColourForRealValue));
			onSingleClick(calculatedY, interval, intervalColours);
		},
		// eslint-disable-next-line
		[intervalsToRenderSignature, lane.depthFrom, onSingleClick, findIntervalAtPosition],
	);

	const [hoverInternal, setHoverInternal] = useState<[number, number]>();
	// Throttle, don't debounce. The previous useStableValue-with-undefined-transient pattern
	// reset the value on every mouse jitter, tearing down the tooltip until the 200ms timer
	// fired. A leading-prev variant pinned the tooltip in place during movement instead, but
	// the tooltip then only refreshed once the mouse stopped — also wrong. useThrottle emits
	// the latest position at most every 50ms, so the tooltip follows the cursor smoothly while
	// the interval-find / setHoveredInterval cascade stays bounded.
	const stableHover = useThrottle(hoverInternal, 50);

	useEffect(() => {
		if (!onHover) return;
		if (!stableHover) {
			onHover(undefined, undefined, undefined);
			return;
		}
		const [x, y] = stableHover;
		const calculatedY = lane.depthFrom + x;
		const interval = findIntervalAtPosition(calculatedY, y);

		const intervalColours = interval?.values?.map((iv) => getIntervalColour(iv, getColourForRealValue));
		onHover(calculatedY, interval, intervalColours);

		// eslint-disable-next-line
	}, [intervalsToRenderSignature, lane.depthFrom, onHover, scale, stableHover, findIntervalAtPosition]);

	const handleHoverInternal = useCallback(
		(pos: [number, number] | undefined) => {
			if (!onHover) return;
			setHoverInternal(pos);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onHover, lane.depthFrom, scale],
	);

	useGesture(
		{
			onClick: (state) => {
				// Skip click events that follow a drag (e.g. releasing a resize handle over another interval)
				if (mouseDownPosRef.current) {
					const dx = state.event.pageX - mouseDownPosRef.current.x;
					const dy = state.event.pageY - mouseDownPosRef.current.y;
					if (dx * dx + dy * dy > DRAG_THRESHOLD_PX_SQ) return;
				}

				// Calculate mouse move from page X/Y, as offsets would trigger for children giving bad values;
				const targetRect = svgRef?.current?.getBoundingClientRect();
				if (!targetRect) return;
				const mouseCoords: [number, number] = [
					(state.event.pageX - targetRect.x) / scale,
					state.event.pageY - targetRect.y,
				];

				if (state.event.detail === 1) handleSingleClickInternal(mouseCoords);
				if (state.event.detail === 2) handleDoubleClickInternal(mouseCoords);
			},
			onMouseMove: (state) => {
				// Calculate mouse move from page X/Y, as offsets would trigger for children giving bad values;
				const targetRect = svgRef?.current?.getBoundingClientRect();
				if (!targetRect) return;

				const mouseCoords: [number, number] = [
					(state.event.pageX - targetRect.x) / scale,
					state.event.pageY - targetRect.y,
				];

				handleHoverInternal(mouseCoords);
			},
			onMouseLeave: () => handleHoverInternal(undefined),
		},
		{ target: svgRef },
	);

	const labelHeightInPx = useMemo(() => {
		return Math.min(heightInPx - 2, 16);
	}, [heightInPx]);

	const measureLabelWidth = useCallback(
		(text: string) => {
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			if (!context) return 0;
			context.font = `${labelHeightInPx}px sans-serif`;
			return context.measureText(text).width;
		},
		[labelHeightInPx],
	);

	return (
		<svg
			ref={svgRef}
			width={width}
			height={heightInPx}
			x={0}
			y={position}
			style={{ cursor: 'default' }}
			onMouseDown={(e) => {
				mouseDownPosRef.current = { x: e.pageX, y: e.pageY };
			}}
		>
			<defs>
				<DiagonalHatchDefinition id="diagonalHatch" stroke={'#555' as HEX} />
				<DiagonalHatchDefinition id="diagonalHatchLight" stroke={'#fff' as HEX} />
				<LoadingGradientDefinition id="loadingPulseGradient" />
				<mask>
					<rect id="mask" x={0} y={0} width={width} height={heightInPx} />
				</mask>
			</defs>
			<rect
				className={styles.background}
				key={'background'}
				x={0}
				y={0}
				width={width}
				height={heightInPx}
				onClick={() => {
					return onSingleClick && onSingleClick(undefined, undefined, undefined);
				}}
				onMouseDown={configuration.canDraw ? handleDrawMouseDown : undefined}
				cursor={configuration.canDraw ? 'cell' : 'default'}
			></rect>
			{[...intervalsToRender]
				.sort((a, b) => {
					const aTrack = trackAssignments.get(a)?.trackIndex ?? 0;
					const bTrack = trackAssignments.get(b)?.trackIndex ?? 0;
					if (aTrack !== bTrack) return aTrack - bTrack;
					return +(a.selected ?? false) - +(b.selected ?? false);
				})
				.map((interval, i) => {
					const renderable = interval.renderableDepth;
					const x = Math.max(0, (renderable.depthFrom - lane.depthFrom) * scale);
					const length =
						(Math.min(lane.depthTo, renderable.depthTo) - Math.max(lane.depthFrom, renderable.depthFrom)) *
						scale;

					const labelX = x + length / 2;
					const intervalValues = interval.values ?? [defaultIntervalValue];
					const intervalColour =
						intervalValues.length === 1
							? getIntervalColour(intervalValues[0], getColourForRealValue)
							: '#FFF';

					const shouldRenderText = trackHeight > TEXT_SIZE_THRESHOLD;

					const canEdit =
						configuration.canEdit &&
						allowableEditRanges?.filter(
							(r) => r.depthFrom <= renderable.depthFrom && r.depthTo >= renderable.depthFrom,
						).length;

					const sanitizedConfigurationId = sanitiseId(configuration.id);
					const { trackIndex } = trackAssignments.get(interval) ?? { trackIndex: 0 };
					const yOffset = trackIndex * trackHeight;
					return (
						<g key={`interval-${i}-${sanitizedConfigurationId}--${lane.depthFrom}-${x}-interval-container`}>
							{intervalValues.map((iv, idx) => {
								const colour = getIntervalColour(iv, getColourForRealValue);
								const labelIsWhite = isYIQDark(colour);
								const intervalValueHeight = trackHeight / intervalValues.length;
								return (
									<g
										key={`${sanitizedConfigurationId}--${lane.depthFrom}-${x}-interval-value-${idx}-container`}
									>
										<rect
											key={`${sanitizedConfigurationId}--${x}-interval`}
											x={x}
											y={yOffset + intervalValueHeight * idx}
											width={length}
											height={intervalValueHeight}
											fill={colour}
										/>
										{labelX > 0 &&
											shouldRenderText &&
											iv.label &&
											!interval.selected &&
											measureLabelWidth(iv.label) < length - 16 &&
											labelHeightInPx < intervalValueHeight && (
												<text
													key={`${sanitizedConfigurationId}--${x}-label`}
													x={labelX}
													y={yOffset + intervalValueHeight * idx + intervalValueHeight / 2}
													style={{
														fill: labelIsWhite ? '#fff' : '#000',
														fontSize: labelHeightInPx,
													}}
													width={length}
													dominantBaseline="middle"
													textAnchor="middle"
												>
													{iv.label}
												</text>
											)}
									</g>
								);
							})}

							{!interval.selected && configuration.endMarkerColour && (
								<>
									{renderable.depthFrom > lane.depthFrom && (
										<>
											<path
												d={`M ${x + 1} ${yOffset + trackHeight / 2} L ${x + 10} ${yOffset + trackHeight / 2 + trackHeight / 4} L ${x + 10} ${yOffset + trackHeight / 2 - trackHeight / 4}`}
												fill={configuration.endMarkerColour(intervalColour)}
												mask={'url(#mask)'}
											/>
											<line
												x1={x}
												y1={yOffset}
												x2={x}
												y2={yOffset + trackHeight}
												stroke={configuration.endMarkerColour(intervalColour)}
												strokeWidth={1}
											/>
										</>
									)}
									{renderable.depthTo < lane.depthTo && (
										<>
											<path
												d={`M ${x + length - 1} ${yOffset + trackHeight / 2} L ${x + length - 10} ${yOffset + trackHeight / 2 + trackHeight / 4} L ${x + length - 10} ${yOffset + trackHeight / 2 - trackHeight / 4}`}
												fill={configuration.endMarkerColour(intervalColour)}
												mask={'url(#mask)'}
											/>
											<line
												x1={x + length}
												y1={yOffset}
												x2={x + length}
												y2={yOffset + trackHeight}
												stroke={configuration.endMarkerColour(intervalColour)}
												strokeWidth={1}
											/>
										</>
									)}
								</>
							)}
							{interval.selected && (
								<>
									<rect
										x={x}
										y={yOffset}
										width={length}
										height={trackHeight}
										fill={`url(#${isYIQDark(intervalColour) ? 'diagonalHatchLight' : 'diagonalHatch'})`}
									/>
									{intervalValues.map((iv, idx) => {
										const colour = getIntervalColour(iv, getColourForRealValue);
										const labelIsWhite = isYIQDark(colour);
										const intervalValueHeight = trackHeight / intervalValues.length;
										if (
											!iv.label ||
											!shouldRenderText ||
											labelX <= 0 ||
											measureLabelWidth(iv.label) >= length - 16 ||
											labelHeightInPx >= intervalValueHeight
										)
											return null;
										const labelWidth = measureLabelWidth(iv.label);
										return (
											<g key={`${sanitizedConfigurationId}--${x}-selected-label-${idx}`}>
												<rect
													x={labelX - labelWidth / 2 - 2}
													y={
														yOffset +
														intervalValueHeight * idx +
														(intervalValueHeight - labelHeightInPx) / 2 -
														2
													}
													width={labelWidth + 4}
													height={labelHeightInPx + 4}
													fill={colour}
												/>
												<text
													x={labelX}
													y={yOffset + intervalValueHeight * idx + intervalValueHeight / 2}
													style={{
														fill: labelIsWhite ? '#fff' : '#000',
														fontSize: labelHeightInPx,
													}}
													width={length}
													dominantBaseline="middle"
													textAnchor="middle"
												>
													{iv.label}
												</text>
											</g>
										);
									})}
									{canEdit && renderable.depthFrom > lane.depthFrom && (
										<circle
											cx={x}
											cy={yOffset + trackHeight / 2}
											r={8}
											fill={intervalColour}
											mask={'url(#mask)'}
											stroke={'black'}
											strokeWidth={'1px'}
											cursor={'w-resize'}
											onMouseDown={() => {
												handleIntervalResizeStarted(
													configuration.intervalSetId,
													interval,
													IntervalResizeHandle.Start,
												);
											}}
										/>
									)}
									{canEdit && renderable.depthTo < lane.depthTo && (
										<circle
											cx={x + length}
											cy={yOffset + trackHeight / 2}
											r={8}
											fill={intervalColour}
											mask={'url(#mask)'}
											stroke={'black'}
											strokeWidth={'1px'}
											cursor={'e-resize'}
											onMouseDown={() => {
												handleIntervalResizeStarted(
													configuration.intervalSetId,
													interval,
													IntervalResizeHandle.End,
												);
											}}
										/>
									)}
								</>
							)}
						</g>
					);
				})}
			{isLoading && (
				<>
					<rect x={0} y={0} width={width} height={heightInPx} fill="url(#loadingPulseGradient)"></rect>
					<rect x={0} y={0} width={width} height={heightInPx} fill="url(#diagonalHatch)" opacity="0.5"></rect>
				</>
			)}
		</svg>
	);
};
export const IntervalLaneRow = memo(IntervalLaneRowInternal);
